/**
 * The feedback hub (docs/feedback.md): one place for bugs, wrong results of the assistant,
 * ideas, change requests and questions, with "My feedback" for the author and a triage
 * view for managers and admins.
 *
 * Request and response shapes follow the contract in docs/feedback.md section 12. The value
 * sets are taken from the generated client; the object shapes are the normalised view the
 * pages use and are checked field by field against the generated models (see the drift
 * check below), so a backend change breaks the type check instead of the page.
 *
 * Everything stays in the app database. No route of this module sends a mail, a Teams
 * message or creates a ticket anywhere; the CSV and the eval export are downloads.
 *
 * Kept free of React so the request shapes, the client context and the error wording
 * can be tested.
 */

import { type ApiError, client } from "./client";
import type {
  FeedbackActivity as GenFeedbackActivity,
  FeedbackClientInfo as GenFeedbackClientInfo,
  FeedbackCommentRequest as GenFeedbackCommentRequest,
  FeedbackCreateRequest as GenFeedbackCreateRequest,
  FeedbackDetail as GenFeedbackDetail,
  FeedbackDuplicateTarget as GenFeedbackDuplicateTarget,
  FeedbackItem as GenFeedbackItem,
  FeedbackList as GenFeedbackList,
  FeedbackRightValue as GenFeedbackRightValue,
  FeedbackSummary as GenFeedbackSummary,
  FeedbackTriageRequest as GenFeedbackTriageRequest,
} from "./generated";
import { detailText } from "./work-actions";

// ─── shared values (section 12.1) ──────────────────────────────────────────
//
// The value sets come from the generated client, so a backend change to an enum shows up
// here at type-check time instead of drifting silently.

export type FeedbackKind = GenFeedbackCreateRequest["kind"];
export type FeedbackHubTopic = NonNullable<GenFeedbackCreateRequest["topic"]>;
export type FeedbackImpact = NonNullable<GenFeedbackCreateRequest["impact"]>;
export type FeedbackFrequency = NonNullable<GenFeedbackCreateRequest["frequency"]>;
export type FeedbackStatus = GenFeedbackItem["status"];
export type FeedbackSurface = NonNullable<GenFeedbackCreateRequest["surface"]>;
export type FeedbackEvalLabel = NonNullable<GenFeedbackDetail["eval_label"]>;
export type FeedbackIntent = NonNullable<GenFeedbackRightValue["intent"]>;

export const FEEDBACK_KINDS: readonly FeedbackKind[] = [
  "bug",
  "model",
  "idea",
  "change",
  "question",
];
export const FEEDBACK_TOPICS: readonly FeedbackHubTopic[] = [
  "customer",
  "item",
  "quantity",
  "price",
  "margin",
  "classification",
  "reply_draft",
  "translation",
  "other",
];
export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = [
  "new",
  "under_review",
  "planned",
  "in_progress",
  "done",
  "wont_do",
  "duplicate",
  "noted",
];
export const FEEDBACK_IMPACTS: readonly FeedbackImpact[] = ["blocks", "slows", "nice"];
export const FEEDBACK_FREQUENCIES: readonly FeedbackFrequency[] = [
  "several_daily",
  "daily",
  "weekly",
  "rarely",
];

/** Longest text field, title and route the server accepts. */
export const FEEDBACK_TEXT_MAX = 2000;
export const FEEDBACK_TITLE_MAX = 120;
export const FEEDBACK_ROUTE_MAX = 300;
export const FEEDBACK_USER_AGENT_MAX = 300;

/** The body keys of each kind, required ones first (section 12.1). */
export const FEEDBACK_BODY_KEYS: Record<FeedbackKind, { required: string[]; optional: string[] }> =
  {
    bug: { required: ["did", "happened"], optional: ["expected"] },
    model: { required: [], optional: ["wrong", "right", "note"] },
    idea: { required: ["problem"], optional: ["suggestion"] },
    change: { required: ["today", "wanted"], optional: ["why"] },
    question: { required: ["question"], optional: [] },
  };

// ─── request and response shapes (sections 12.2 to 12.6) ───────────────────

/** The structured "what is right" of a model item; every key optional. */
export interface FeedbackRightValue {
  account?: string;
  item?: string;
  quantity?: number;
  unit_price?: number;
  currency?: string;
  intent?: FeedbackIntent;
}

export type FeedbackBody = Record<string, string | null | FeedbackRightValue | undefined> & {
  right_value?: FeedbackRightValue;
};

/** What only the browser knows; the server fills in everything else. */
export interface FeedbackClientContext {
  app_version: string;
  user_agent: string;
  language: string;
  viewport: string;
  time_zone: string;
  client_time: string;
}

/** `POST /feedback` (createFeedback). */
export interface FeedbackCreateRequest {
  kind: FeedbackKind;
  topic: FeedbackHubTopic | null;
  verdict: "right" | "wrong" | null;
  title: string | null;
  body: FeedbackBody;
  impact: FeedbackImpact | null;
  frequency: FeedbackFrequency | null;
  surface: FeedbackSurface;
  route: string | null;
  case_id: string | null;
  line_index: number | null;
  area: string | null;
  include_mail_text: boolean;
  /** Translation feedback: the language the mail was translated into (two letters). */
  target_language?: string | null;
  client: FeedbackClientContext;
}

export interface FeedbackItem {
  feedback_id: number;
  ref: string;
  kind: FeedbackKind;
  topic: FeedbackHubTopic | null;
  verdict: "right" | "wrong" | null;
  title: string | null;
  status: FeedbackStatus;
  impact: FeedbackImpact | null;
  area: string | null;
  case_id: string | null;
  line_index: number | null;
  surface: FeedbackSurface;
  created_at: string;
  updated_at: string;
  unread: boolean;
  duplicate_of: number | null;
  reply_count: number;
}

export interface FeedbackList {
  items: FeedbackItem[];
  total: number;
}

export interface FeedbackActivity {
  activity_id: number;
  kind: GenFeedbackActivity["kind"];
  actor: string;
  at: string;
  text: string | null;
  internal: boolean;
  detail?: Record<string, unknown> | null;
}

/** The item a duplicate is linked to: its status and author-visible replies. */
export interface FeedbackDuplicateTarget {
  feedback_id: number;
  ref: string;
  status: FeedbackStatus;
  updated_at: string;
  activities: FeedbackActivity[];
}

export interface FeedbackDetail extends FeedbackItem {
  body: FeedbackBody;
  frequency: FeedbackFrequency | null;
  include_mail_text: boolean;
  context: Record<string, unknown>;
  activities: FeedbackActivity[];
  duplicate_count: number;
  /** For a duplicate: the item that is followed, with its author-visible replies. */
  duplicate_target?: FeedbackDuplicateTarget | null;
  /** Triager only from here on; absent for the author. */
  author?: string;
  owner?: string | null;
  version?: number;
  eval_label?: FeedbackEvalLabel | null;
  superseded_by?: number | null;
  case_visible?: boolean;
  snapshot?: Record<string, unknown> | null;
  mail_excerpt?: string | null;
}

export interface FeedbackSummary {
  total: number;
  by_kind: Record<string, number>;
  by_status: Record<string, number>;
  by_area: Record<string, number>;
  by_impact: Record<string, number>;
}

/** `PATCH /feedback/{id}` (triageFeedback); `version` is required. */
export interface FeedbackTriageRequest {
  version: number;
  status?: FeedbackStatus;
  duplicate_of?: number | null;
  owner?: string | null;
  kind?: FeedbackKind;
  topic?: FeedbackHubTopic | null;
  area?: string | null;
  impact?: FeedbackImpact | null;
  eval_label?: FeedbackEvalLabel;
  reply?: string;
}

export interface FeedbackCommentRequest {
  text: string;
  internal: boolean;
}

/** The filters of the triage list, summary and CSV export (section 12.5). */
export interface FeedbackFilters {
  kind?: FeedbackKind | null;
  status?: FeedbackStatus | null;
  area?: string | null;
  topic?: FeedbackHubTopic | null;
  impact?: FeedbackImpact | null;
  case_id?: string | null;
  owner?: string | null;
  q?: string | null;
  from?: string | null;
  to?: string | null;
  include_noted?: boolean;
  include_superseded?: boolean;
  limit?: number;
  offset?: number;
}

export interface MyFeedbackFilters {
  status?: FeedbackStatus | null;
  kind?: FeedbackKind | null;
  limit?: number;
  offset?: number;
}

// ─── drift check against the generated client ──────────────────────────────
//
// The shapes above are the normalised view the pages code against (lists always present,
// counts always numbers). Every field they name must exist in the generated model with a
// compatible type; a renamed or retyped backend field fails `tsc` here. Nested lists of
// activities are covered by the FeedbackActivity row.

type FieldsFit<Hand, Gen> = {
  [K in keyof Hand]-?: K extends keyof Gen
    ? NonNullable<Hand[K]> extends NonNullable<Gen[K]>
      ? true
      : false
    : false;
}[keyof Hand];
type AssertFits<T extends true> = T;

export type FeedbackContractCheck = [
  AssertFits<FieldsFit<Omit<FeedbackItem, "body">, GenFeedbackItem>>,
  AssertFits<FieldsFit<FeedbackList, GenFeedbackList>>,
  AssertFits<FieldsFit<FeedbackActivity, GenFeedbackActivity>>,
  AssertFits<FieldsFit<Omit<FeedbackDuplicateTarget, "activities">, GenFeedbackDuplicateTarget>>,
  AssertFits<
    FieldsFit<Omit<FeedbackDetail, "body" | "activities" | "duplicate_target">, GenFeedbackDetail>
  >,
  AssertFits<FieldsFit<FeedbackSummary, GenFeedbackSummary>>,
  AssertFits<FieldsFit<FeedbackTriageRequest, GenFeedbackTriageRequest>>,
  AssertFits<FieldsFit<FeedbackCommentRequest, GenFeedbackCommentRequest>>,
  AssertFits<FieldsFit<FeedbackRightValue, GenFeedbackRightValue>>,
  AssertFits<FieldsFit<FeedbackClientContext, GenFeedbackClientInfo>>,
  AssertFits<FieldsFit<Omit<FeedbackCreateRequest, "body" | "client">, GenFeedbackCreateRequest>>,
];

// ─── client context ────────────────────────────────────────────────────────

/** The version baked into the bundle at build time (`RFQ_BUILD_SHA`), else "dev". */
export function bundleVersion(): string {
  const raw: unknown = import.meta.env.VITE_BUILD_SHA;
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : "dev";
}

interface ContextSource {
  navigator?: { userAgent?: string; language?: string } | null;
  window?: { innerWidth?: number; innerHeight?: number } | null;
  timeZone?: string | null;
  now?: Date;
  appVersion?: string;
}

/**
 * What the browser adds: the user agent (cut to 300 characters), the browser language,
 * the viewport size, the time zone and the clock. Nothing else is read.
 */
export function clientContext(source: ContextSource = {}): FeedbackClientContext {
  const nav = source.navigator ?? (typeof navigator === "undefined" ? null : navigator);
  const win = source.window ?? (typeof window === "undefined" ? null : window);
  let timeZone = source.timeZone ?? "";
  if (!timeZone) {
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    } catch {
      timeZone = "";
    }
  }
  const width = Math.round(win?.innerWidth ?? 0);
  const height = Math.round(win?.innerHeight ?? 0);
  return {
    app_version: source.appVersion ?? bundleVersion(),
    user_agent: (nav?.userAgent ?? "").slice(0, FEEDBACK_USER_AGENT_MAX),
    language: nav?.language ?? "",
    viewport: width > 0 && height > 0 ? `${width}x${height}` : "",
    time_zone: timeZone,
    client_time: (source.now ?? new Date()).toISOString().replace(/\.\d{3}Z$/, "Z"),
  };
}

/** The path of a location only: no query string and no hash (they can hold search words). */
export function routeOf(pathname: string | null | undefined): string | null {
  const raw = (pathname ?? "").split(/[?#]/)[0]?.trim() ?? "";
  return raw === "" ? null : raw.slice(0, FEEDBACK_ROUTE_MAX);
}

// ─── the form, validated and turned into a request ─────────────────────────

/** What the dialog holds; every text untrimmed, as typed. */
export interface FeedbackFormState {
  kind: FeedbackKind | null;
  topic: FeedbackHubTopic | null;
  title: string;
  fields: Record<string, string>;
  /** For model items: the shaped "what is right" inputs, as typed. */
  right: {
    account?: string;
    item?: string;
    quantity?: string;
    unit_price?: string;
    currency?: string;
    intent?: FeedbackIntent | "";
  };
  impact: FeedbackImpact | null;
  frequency: FeedbackFrequency | null;
  includeMailText: boolean;
  area: string | null;
}

/** Where the dialog was opened and what the entry knows. */
export interface FeedbackTarget {
  surface: FeedbackSurface;
  caseId?: string | null;
  lineIndex?: number | null;
  /** Translation feedback: the language the mail was translated into. */
  targetLanguage?: string | null;
}

export function emptyFeedbackForm(
  kind: FeedbackKind | null = null,
  topic: FeedbackHubTopic | null = null,
): FeedbackFormState {
  return {
    kind,
    topic: kind === "model" ? topic : null,
    title: "",
    fields: {},
    right: {},
    impact: null,
    frequency: null,
    includeMailText: false,
    area: null,
  };
}

/** The model topics whose "what is right" is a free text (`body.right`). */
export const RIGHT_AS_TEXT: readonly FeedbackHubTopic[] = [
  "margin",
  "reply_draft",
  "translation",
  "other",
];

/** Error text per field key (`title`, `topic`, a body key, or `right.<key>`); empty when valid. */
export type FeedbackFormErrors = Record<string, string>;

const REQUIRED_TEXT = "Please fill this in.";

function number(raw: string | undefined): number | null {
  const text = (raw ?? "").trim().replace(",", ".");
  if (text === "") {
    return null;
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** Check the form against the rules of section 4 and 12. */
export function validateFeedbackForm(
  form: FeedbackFormState,
  options: { seeMargin?: boolean } = {},
): FeedbackFormErrors {
  const errors: FeedbackFormErrors = {};
  if (!form.kind) {
    errors.kind = "Pick what kind of feedback this is.";
    return errors;
  }
  if (form.title.trim().length > FEEDBACK_TITLE_MAX) {
    errors.title = `At most ${FEEDBACK_TITLE_MAX} characters.`;
  }
  const keys = FEEDBACK_BODY_KEYS[form.kind];
  for (const key of keys.required) {
    if ((form.fields[key] ?? "").trim() === "") {
      errors[key] = REQUIRED_TEXT;
    }
  }
  for (const key of [...keys.required, ...keys.optional]) {
    if ((form.fields[key] ?? "").trim().length > FEEDBACK_TEXT_MAX) {
      errors[key] = `At most ${FEEDBACK_TEXT_MAX} characters.`;
    }
  }
  if (form.kind === "model") {
    if (!form.topic) {
      errors.topic = "Pick what was wrong.";
    } else if (form.topic === "margin" && !options.seeMargin) {
      errors.topic = "Pick what was wrong.";
    }
    const quantity = number(form.right.quantity);
    if (quantity !== null && !(quantity > 0)) {
      errors["right.quantity"] = "Enter a quantity above 0.";
    }
    const price = number(form.right.unit_price);
    if (price !== null && !(price >= 0)) {
      errors["right.unit_price"] = "Enter a price of 0 or more.";
    }
    if ((form.right.account ?? "").trim().length > 40) {
      errors["right.account"] = "At most 40 characters.";
    }
    if ((form.right.item ?? "").trim().length > 60) {
      errors["right.item"] = "At most 60 characters.";
    }
    if ((form.right.currency ?? "").trim().length > 3) {
      errors["right.currency"] = "Three letters, for example EUR.";
    }
  }
  return errors;
}

/** The structured "what is right" for the topic; only the keys the topic asks for. */
export function rightValueOf(form: FeedbackFormState): FeedbackRightValue {
  const value: FeedbackRightValue = {};
  const text = (raw: string | undefined) => (raw ?? "").trim();
  switch (form.topic) {
    case "customer":
      if (text(form.right.account)) {
        value.account = text(form.right.account);
      }
      break;
    case "item":
      if (text(form.right.item)) {
        value.item = text(form.right.item);
      }
      break;
    case "quantity": {
      const quantity = number(form.right.quantity);
      if (quantity !== null && quantity > 0) {
        value.quantity = quantity;
      }
      break;
    }
    case "price": {
      const price = number(form.right.unit_price);
      if (price !== null && price >= 0) {
        value.unit_price = price;
      }
      if (text(form.right.currency)) {
        value.currency = text(form.right.currency).toUpperCase();
      }
      break;
    }
    case "classification":
      if (form.right.intent) {
        value.intent = form.right.intent;
      }
      break;
    default:
      break;
  }
  return value;
}

/**
 * The request body of `POST /feedback`. Texts are trimmed; empty optional texts are
 * left out; topic, verdict and frequency only where the kind takes them; the mail tick
 * only with a case. Throws a RangeError when the form is not valid.
 */
export function buildFeedbackRequest(
  form: FeedbackFormState,
  target: FeedbackTarget,
  context: { route: string | null; client: FeedbackClientContext; seeMargin?: boolean },
): FeedbackCreateRequest {
  const errors = validateFeedbackForm(form, { seeMargin: context.seeMargin });
  const first = Object.values(errors)[0];
  if (first || !form.kind) {
    throw new RangeError(first ?? "Pick what kind of feedback this is.");
  }
  const kind = form.kind;
  const keys = FEEDBACK_BODY_KEYS[kind];
  const body: FeedbackBody = {};
  for (const key of [...keys.required, ...keys.optional]) {
    const value = (form.fields[key] ?? "").trim();
    if (value !== "") {
      body[key] = value;
    }
  }
  if (kind === "model") {
    // "What is right" as text only for the topics that ask for text.
    if (!form.topic || !RIGHT_AS_TEXT.includes(form.topic)) {
      delete body.right;
    }
    const right = rightValueOf(form);
    if (Object.keys(right).length > 0) {
      body.right_value = right;
    }
  }
  const caseId = (target.caseId ?? "").trim() || null;
  const lineIndex =
    caseId && typeof target.lineIndex === "number" && target.lineIndex >= 0
      ? target.lineIndex
      : null;
  const title = form.title.trim();
  return {
    kind,
    topic: kind === "model" ? form.topic : null,
    verdict: kind === "model" ? "wrong" : null,
    title: title === "" ? null : title,
    body,
    impact: form.impact,
    frequency: kind === "idea" ? form.frequency : null,
    surface: target.surface,
    route: routeOf(context.route),
    case_id: caseId,
    line_index: lineIndex,
    area: caseId ? null : form.area,
    include_mail_text: Boolean(caseId) && form.includeMailText,
    ...(caseId && /^[a-z]{2}$/.test(target.targetLanguage ?? "")
      ? { target_language: target.targetLanguage }
      : {}),
    client: context.client,
  };
}

// ─── requests ──────────────────────────────────────────────────────────────

/** A failed hub request; on 429 it carries the wait from `Retry-After`. */
export interface FeedbackHubError extends ApiError {
  retryAfterSeconds?: number | null;
}

function retryAfter(header: string | null | undefined, now: Date = new Date()): number | null {
  const raw = (header ?? "").trim();
  if (raw === "") {
    return null;
  }
  if (/^\d+$/.test(raw)) {
    return Number.parseInt(raw, 10);
  }
  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : Math.max(0, Math.round((at - now.getTime()) / 1000));
}

type Method = "GET" | "POST" | "PATCH";

async function send<T>(
  method: Method,
  url: string,
  options: {
    path?: Record<string, string | number>;
    query?: Record<string, unknown>;
    body?: unknown;
  } = {},
): Promise<T> {
  const result = await client.request<{ 200: T }, unknown, false>({
    method,
    url,
    path: options.path,
    query: options.query,
    ...(options.body === undefined
      ? {}
      : { body: options.body, headers: { "Content-Type": "application/json" } }),
    throwOnError: false,
  });
  if (result.error !== undefined || !result.response?.ok) {
    const failure = (result.error ?? {}) as Partial<ApiError>;
    const thrown: FeedbackHubError = {
      status: failure.status ?? result.response?.status ?? 0,
      body: failure.body ?? null,
    };
    if (thrown.status === 429) {
      thrown.retryAfterSeconds = retryAfter(result.response?.headers.get("Retry-After"));
    }
    throw thrown;
  }
  return result.data as T;
}

/** Leave out empty filter values, so the query string carries only what is set. */
export function feedbackQuery(
  filters: FeedbackFilters | MyFeedbackFilters = {},
): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === "boolean") {
      if (value) {
        query[key] = "true";
      }
      continue;
    }
    const text = String(value).trim();
    if (text !== "") {
      query[key] = text;
    }
  }
  return query;
}

function toList(raw: unknown): FeedbackList {
  const data = raw as Partial<FeedbackList> | null;
  const items = Array.isArray(data?.items)
    ? data.items.filter(
        (row): row is FeedbackItem =>
          row != null &&
          typeof row === "object" &&
          typeof (row as FeedbackItem).feedback_id === "number",
      )
    : [];
  return { items, total: typeof data?.total === "number" ? data.total : items.length };
}

export const feedbackHubKeys = {
  all: ["feedback-hub"] as const,
  mine: (filters: MyFeedbackFilters = {}) =>
    ["feedback-hub", "mine", feedbackQuery(filters)] as const,
  unread: () => ["feedback-hub", "unread"] as const,
  detail: (id: number) => ["feedback-hub", "detail", id] as const,
  triage: (filters: FeedbackFilters = {}) =>
    ["feedback-hub", "triage", feedbackQuery(filters)] as const,
  summary: (filters: FeedbackFilters = {}) =>
    ["feedback-hub", "summary", feedbackQuery(filters)] as const,
};

/** `POST /feedback` (createFeedback). */
export function createFeedback(request: FeedbackCreateRequest): Promise<FeedbackItem> {
  return send<FeedbackItem>("POST", "/feedback", { body: request });
}

/** `GET /feedback/mine` (listMyFeedback). */
export async function listMyFeedback(filters: MyFeedbackFilters = {}): Promise<FeedbackList> {
  return toList(await send<unknown>("GET", "/feedback/mine", { query: feedbackQuery(filters) }));
}

/** `GET /feedback/unread-count`; 0 on an older backend (404). */
export async function getFeedbackUnreadCount(): Promise<number> {
  try {
    const data = await send<{ unread?: unknown }>("GET", "/feedback/unread-count");
    return typeof data?.unread === "number" && data.unread > 0 ? data.unread : 0;
  } catch (error) {
    if ((error as ApiError | null)?.status === 404) {
      return 0;
    }
    throw error;
  }
}

/** `GET /feedback/{id}` (getFeedback). */
export function getFeedback(feedbackId: number): Promise<FeedbackDetail> {
  return send<FeedbackDetail>("GET", "/feedback/{feedback_id}", {
    path: { feedback_id: feedbackId },
  });
}

/** `POST /feedback/{id}/seen` (markFeedbackSeen), 204. */
export async function markFeedbackSeen(feedbackId: number): Promise<void> {
  await send<unknown>("POST", "/feedback/{feedback_id}/seen", {
    path: { feedback_id: feedbackId },
    body: {},
  });
}

/** `POST /feedback/{id}/comments` (addFeedbackComment). Throws a RangeError on a bad text. */
export function addFeedbackComment(
  feedbackId: number,
  request: FeedbackCommentRequest,
): Promise<FeedbackActivity> {
  const text = request.text.trim();
  if (text === "") {
    return Promise.reject(new RangeError("Write a few words first."));
  }
  if (text.length > FEEDBACK_TEXT_MAX) {
    return Promise.reject(new RangeError(`At most ${FEEDBACK_TEXT_MAX} characters.`));
  }
  return send<FeedbackActivity>("POST", "/feedback/{feedback_id}/comments", {
    path: { feedback_id: feedbackId },
    body: { text, internal: request.internal },
  });
}

/** `GET /feedback` (listFeedback), triagers only. */
export async function listFeedback(filters: FeedbackFilters = {}): Promise<FeedbackList> {
  return toList(await send<unknown>("GET", "/feedback", { query: feedbackQuery(filters) }));
}

/** `GET /feedback/summary` (getFeedbackSummary), triagers only. */
export async function getFeedbackSummary(filters: FeedbackFilters = {}): Promise<FeedbackSummary> {
  const { limit: _limit, offset: _offset, ...rest } = filters;
  const data = await send<Partial<FeedbackSummary> | null>("GET", "/feedback/summary", {
    query: feedbackQuery(rest),
  });
  const counts = (raw: unknown): Record<string, number> =>
    raw && typeof raw === "object"
      ? Object.fromEntries(
          Object.entries(raw as Record<string, unknown>).filter(
            (entry): entry is [string, number] => typeof entry[1] === "number",
          ),
        )
      : {};
  return {
    total: typeof data?.total === "number" ? data.total : 0,
    by_kind: counts(data?.by_kind),
    by_status: counts(data?.by_status),
    by_area: counts(data?.by_area),
    by_impact: counts(data?.by_impact),
  };
}

/** `PATCH /feedback/{id}` (triageFeedback). Empty reply and unchanged keys are left out. */
export function triageFeedback(
  feedbackId: number,
  request: FeedbackTriageRequest,
): Promise<FeedbackDetail> {
  const body: FeedbackTriageRequest = { version: request.version };
  for (const [key, value] of Object.entries(request) as [keyof FeedbackTriageRequest, unknown][]) {
    if (key === "version" || value === undefined) {
      continue;
    }
    if (key === "reply") {
      const reply = typeof value === "string" ? value.trim() : "";
      if (reply.length > FEEDBACK_TEXT_MAX) {
        return Promise.reject(new RangeError(`At most ${FEEDBACK_TEXT_MAX} characters.`));
      }
      if (reply !== "") {
        body.reply = reply;
      }
      continue;
    }
    if (key === "owner") {
      const owner = typeof value === "string" ? value.trim() : null;
      body.owner = owner === "" ? null : owner;
      continue;
    }
    (body as unknown as Record<string, unknown>)[key] = value;
  }
  if (body.status === "duplicate" && typeof body.duplicate_of !== "number") {
    return Promise.reject(new RangeError("Enter the number of the item this one repeats."));
  }
  return send<FeedbackDetail>("PATCH", "/feedback/{feedback_id}", {
    path: { feedback_id: feedbackId },
    body,
  });
}

function withQuery(path: string, filters: Record<string, string>): string {
  const query = new URLSearchParams(filters).toString();
  return query === "" ? path : `${path}?${query}`;
}

/** The CSV download of the filtered triage list (texts only, never snapshot or mail). */
export function feedbackExportUrl(filters: FeedbackFilters = {}): string {
  const { limit: _limit, offset: _offset, ...rest } = filters;
  return withQuery("/api/feedback/export.csv", feedbackQuery(rest));
}

/** The JSON Lines download of model items as candidate eval rows (silver tier). */
export function feedbackEvalExportUrl(
  filters: {
    area?: string | null;
    topic?: FeedbackHubTopic | null;
    label?: "accepted" | "candidate" | "all";
    from?: string | null;
    to?: string | null;
  } = {},
): string {
  return withQuery("/api/feedback/eval-export", feedbackQuery(filters));
}

/** "FB-121" from an id or a typed reference; null when it is not one. */
export function parseFeedbackRef(raw: string | number | null | undefined): number | null {
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  }
  const match = /^\s*(?:fb-?)?(\d{1,12})\s*$/i.exec(raw ?? "");
  const id = match ? Number.parseInt(match[1] ?? "", 10) : Number.NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

// ─── wording of failures ───────────────────────────────────────────────────

function waitText(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes >= 60) {
    return "an hour";
  }
  return minutes === 1 ? "a minute" : `${minutes} minutes`;
}

/** What a failed hub request reads as. Raw server text of a 5xx never reaches the screen. */
export function feedbackHubFailureText(
  error: unknown,
  what: "create" | "comment" | "triage" | "read" = "create",
): string {
  if (error instanceof RangeError) {
    return error.message;
  }
  const { status, body, retryAfterSeconds: wait } = (error ?? {}) as Partial<FeedbackHubError>;
  if (status === 429) {
    const lead =
      what === "comment"
        ? "You wrote many replies on this item in the last hour."
        : "You gave a lot of feedback in the last hour.";
    return typeof wait === "number"
      ? `${lead} Try again in ${waitText(wait)}.`
      : `${lead} Try again later.`;
  }
  if (status === 409) {
    return "Someone else changed this item in the meantime. It was loaded again; check and save once more.";
  }
  if (status === 422) {
    return detailText(body) ?? "The server did not accept this. Check the fields and try again.";
  }
  if (status === 403) {
    return "Your role does not allow this.";
  }
  if (status === 404) {
    return what === "create"
      ? "This case is not in your areas, so feedback on it cannot be saved."
      : "This feedback item does not exist or is not yours to see.";
  }
  if (status === 401) {
    return "Your session has ended. Sign in again.";
  }
  if (status === 503) {
    return "Feedback cannot be saved right now. Try again later.";
  }
  return what === "read"
    ? "This could not be loaded. Try again in a moment."
    : "This could not be saved. Try again in a moment.";
}
